import { apiFetch } from "../../api/client";
import type {
  AdminContactDetail,
  AdminContactImportResult,
  AdminContactImportRow,
  AdminContactListParams,
  AdminContactPage,
  AdminShowOption,
  SuppressContactRequest,
} from "../../api/types";

// User follow-up 2026-09-22 ("cuando aplico el filtro de Activo dice sin
// contactos que coincidan"): `contact.estado`/`contact.nivel_jerarquico` are
// stored lowercase/snake_case in Postgres (`ck_contact_estado`/
// `ck_contact_nivel` CHECK constraints) — the SAME values
// `AdminContactSqlBuilder.BuildListQuery` compares against with a plain
// `=`, no case-insensitive match. `ContactStatus`/`HierarchyLevel` (this
// file's own types) spell those same values PascalCase for JSON responses
// (`ParseContactStatus`/`ParseHierarchyLevel` convert DB → PascalCase on
// read) — nothing converted the other way for this filter, so
// `?estado=Activo` compared against a stored `'activo'` and matched zero
// rows. `nivelJerarquico`'s five values are single words, so a bare
// `toLowerCase()` already reproduces the DB's spelling; `estado`'s
// "EnNegociacion" needs an explicit split into `en_negociacion`.
const ESTADO_QUERY_VALUES: Record<string, string> = {
  Activo: "activo",
  Suprimido: "suprimido",
  EnNegociacion: "en_negociacion",
};

function toEstadoQueryValue(estado: string): string {
  return ESTADO_QUERY_VALUES[estado] ?? estado.toLowerCase();
}

function buildQueryString(params: AdminContactListParams): string {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.estado) search.set("estado", toEstadoQueryValue(params.estado));
  if (params.nivelJerarquico) search.set("nivelJerarquico", params.nivelJerarquico.toLowerCase());
  if (params.industria) search.set("industria", params.industria);
  // issue #274: showId is a GUID (AdminContactQuery.ShowId), not a closed vocabulary like
  // estado/nivelJerarquico -- travels as-is, same camelCase query key
  // AdminContactsFunction.List already reads (query["showId"]) and the same pattern already
  // used for AdminAudienceFilterPreviewParams.showId (campaigns/api.ts).
  if (params.showId) search.set("showId", params.showId);
  search.set("page", String(params.page ?? 1));
  search.set("size", String(params.size ?? 20));
  return search.toString();
}

/** `GET admin/contacts?...` (role Consulta). */
export function listContacts(params: AdminContactListParams): Promise<AdminContactPage> {
  return apiFetch<AdminContactPage>(`/panel/contacts?${buildQueryString(params)}`);
}

/** `GET admin/contacts/{id}` (role Consulta). */
export function getContact(id: string): Promise<AdminContactDetail> {
  return apiFetch<AdminContactDetail>(`/panel/contacts/${id}`);
}

/** `POST admin/contacts/{id}/suppress` (role Operador). */
export function suppressContact(id: string, body: SuppressContactRequest): Promise<void> {
  return apiFetch<void>(`/panel/contacts/${id}/suppress`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** `DELETE admin/contacts/{id}/suppression` (role Operador). */
export function unsuppressContact(id: string): Promise<void> {
  return apiFetch<void>(`/panel/contacts/${id}/suppression`, {
    method: "DELETE",
  });
}

/**
 * `POST admin/contacts/import` (role Operador) — bulk contact import from a
 * CSV/XLSX parsed client-side (`admin-api` "Contactos — carga masiva via
 * import"). Body is `{showId, contacts}` (epic #236 tarea #238, work unit 2):
 * `showId` must belong to an existing Show for this client (404 otherwise)
 * and gets associated via `contact_show` to every row that ends up inserted
 * or already existed. No `clientId` in `contacts`: the server always
 * resolves it from `CLIENT_ID` server-side config, never from the payload.
 */
export function importContacts(showId: string, rows: AdminContactImportRow[]): Promise<AdminContactImportResult> {
  return apiFetch<AdminContactImportResult>("/panel/contacts/import", {
    method: "POST",
    body: JSON.stringify({ showId, contacts: rows }),
  });
}

/**
 * `GET panel/show-options` (role Consulta) — bounded (`size<=50`) selector
 * read for the contact-import panel's Show selector (epic #236 tarea #238,
 * work unit 4), same "options" pattern as `getTemplateOptions`. Fetched once
 * per panel mount, never per keystroke.
 */
export function getShowOptions(): Promise<AdminShowOption[]> {
  return apiFetch<AdminShowOption[]>("/panel/show-options");
}

/**
 * `POST panel/shows` (role Operador) — get-or-create by name
 * (feat/show-create-on-import): lets `ShowSelector` create a Show inline
 * when the operator types a name that doesn't exist yet. Returns the
 * EXISTING Show (unchanged) if the normalized name already matches one --
 * never creates a duplicate.
 */
export function createOrGetShow(nombre: string): Promise<AdminShowOption> {
  return apiFetch<AdminShowOption>("/panel/shows", {
    method: "POST",
    body: JSON.stringify({ nombre }),
  });
}
